"use client";

import { useState, useCallback, RefObject, useEffect } from "react";

// =============================================
// Constantes
// =============================================
export const DEBOUNCE_DELAY = 300; // ms
export const MOBILE_BREAKPOINT = 768; // px

// =============================================
// Tipos
// =============================================
export type OutsideClickHandler = (event: Event) => void;

/**
 * Hook para aplicar debounce em valores.
 * @param value - Valor a ser debounced.
 * @param delay - Tempo de atraso em ms (padrão: 300).
 * @returns Valor debounced.
 */
export function useDebounce<T>(value: T, delay: number = DEBOUNCE_DELAY): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Hook para detectar se está em dispositivo mobile.
 * @returns Boolean indicando se é mobile (undefined no SSR inicial).
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(max-width: ${MOBILE_BREAKPOINT - 1}px)`
    );

    const updateState = () => setIsMobile(mediaQuery.matches);

    mediaQuery.addEventListener("change", updateState);
    updateState();

    return () => mediaQuery.removeEventListener("change", updateState);
  }, []);

  return isMobile;
}

/**
 * Hook para detectar cliques fora de um elemento.
 * @param ref - Referência do elemento.
 * @param handler - Função chamada ao clicar fora.
 */
export function useOutsideClick(
  ref: RefObject<HTMLElement>,
  handler: OutsideClickHandler
) {
  const memoizedHandler = useCallback(handler, [handler]);

  useEffect(() => {
    const listener = (event: Event) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return;
      memoizedHandler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, memoizedHandler]);
}
